import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { withSite } from "@/lib/site";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, fmtDateTime, relativeTime } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { BatchEditor } from "@/lib/batch/BatchEditor";
import { reconcile, markDuplicates } from "@/lib/batch/batchCore";
import { Plus, Flame, Pencil, Trash2, ListChecks, Layers, User } from "lucide-react";

const UNASSIGNED = "__unassigned__";

function endOfTodayLocalInput() {
  const d = new Date();
  d.setHours(23, 59, 0, 0);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const signalTasksChanged = () => window.dispatchEvent(new Event("cc-tasks-changed"));

export default function Prep() {
  const { has } = useAuth();
  const [active, setActive] = useState([]);
  const [done, setDone] = useState([]);
  const [people, setPeople] = useState([]);
  const [mine, setMine] = useState({ linked: false, tasks: [] });
  const [modal, setModal] = useState(false);
  const [batch, setBatch] = useState(false);
  const [edit, setEdit] = useState(null);
  const [filter, setFilter] = useState("all");

  const canManage = has("manage_prep");
  const canComplete = has("complete_prep");

  const load = async () => {
    const reqs = [
      api.get(withSite("/prep?status=active")),
      api.get(withSite("/prep?status=completed")),
      api.get("/prep/my-tasks"),
    ];
    if (canManage || canComplete) reqs.push(api.get(withSite("/prep/assignable-people")));
    const [a, d, mt, pp] = await Promise.all(reqs);
    setActive(a.data); setDone(d.data); setMine(mt.data);
    if (pp) setPeople(pp.data);
  };
  useEffect(() => { load(); const h = () => load(); window.addEventListener("cc-site-changed", h); return () => window.removeEventListener("cc-site-changed", h); /* eslint-disable-next-line */ }, []);

  const myPid = mine.linked ? mine.person?.person_id : null;
  const matches = (p) =>
    filter === "all" ? true :
    filter === "unassigned" ? !p.assigned_to_person_id :
    filter === "me" ? (myPid && p.assigned_to_person_id === myPid) :
    p.assigned_to_person_id === filter;
  const activeFiltered = active.filter(matches);

  const completeTask = async (id) => { await api.post(`/prep/${id}/complete`); toast.success("Prep completed"); signalTasksChanged(); load(); };

  return (
    <div>
      <PageHeader title="Prep list" subtitle="Add jobs, flag urgent, assign to your team, track who did what." testid="prep-header"
        action={canComplete || canManage ? (
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-xl" data-testid="batch-add-prep-btn" onClick={() => setBatch(true)}><Layers size={18} className="mr-1" /> Batch add</Button>
            <Button className="bg-brand hover:bg-brand-hover rounded-xl" data-testid="add-prep-btn" onClick={() => setModal(true)}><Plus size={18} className="mr-1" /> Add task</Button>
          </div>
        ) : null} />

      <Tabs defaultValue={mine.linked ? "mine" : "active"}>
        <TabsList className="mb-5 flex-wrap h-auto">
          <TabsTrigger value="active" data-testid="tab-active-prep">Active ({active.length})</TabsTrigger>
          {mine.linked && <TabsTrigger value="mine" data-testid="tab-my-prep">My prep tasks</TabsTrigger>}
          <TabsTrigger value="completed" data-testid="tab-completed-prep">Completed tasks</TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="space-y-2">
          {(canManage || canComplete) && (
            <div className="flex items-center gap-2 mb-1 flex-wrap" data-testid="prep-filter-bar">
              <Label className="text-xs text-slate-500">Filter</Label>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-60 h-9" data-testid="prep-assignee-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {myPid && <SelectItem value="me">Assigned to me</SelectItem>}
                  {people.map((p) => <SelectItem key={p.person_id} value={p.person_id}>{p.display_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {activeFiltered.length === 0 ? <EmptyState icon={ListChecks} title="No prep tasks." message="You're all caught up." /> :
            activeFiltered.map((p) => (
              <div key={p.prep_id} className={`cc-card p-4 ${p.urgent ? "border-red-200" : ""}`} data-testid={`prep-${p.prep_id}`}>
                <div className="flex items-start gap-3">
                  {canComplete && <Checkbox className="mt-1" data-testid={`complete-prep-${p.prep_id}`} onCheckedChange={() => completeTask(p.prep_id)} />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-800 break-words">{p.title}</span>
                      {p.urgent && <span className="text-[10px] font-bold uppercase bg-red-100 text-red-600 px-2 py-0.5 rounded-full flex items-center gap-1"><Flame size={11} /> Urgent</span>}
                      {p.assigned_to_name_snapshot
                        ? <span className="text-[10px] font-semibold bg-brand-light text-brand-dark px-2 py-0.5 rounded-full flex items-center gap-1 max-w-[160px] truncate" title={p.assigned_to_name_snapshot} data-testid={`prep-assignee-${p.prep_id}`}><User size={11} /> <span className="truncate">{p.assigned_to_name_snapshot}</span></span>
                        : <span className="text-[10px] text-slate-400" data-testid={`prep-unassigned-${p.prep_id}`}>Unassigned</span>}
                    </div>
                    {p.details && <div className="text-xs text-slate-500 mt-0.5 break-words">{p.details}</div>}
                    <div className="text-[11px] text-slate-400 mt-1">By: {p.created_by_name} · Added {fmtDateTime(p.created_at)} · Due {p.due_at ? relativeTime(p.due_at) : "—"}</div>
                  </div>
                  {canManage && <div className="flex gap-1 shrink-0">
                    <button data-testid={`edit-prep-${p.prep_id}`} onClick={() => setEdit(p)} className="p-1.5 text-slate-400 hover:text-slate-700"><Pencil size={15} /></button>
                    <button data-testid={`delete-prep-${p.prep_id}`} onClick={async () => { await api.delete(`/prep/${p.prep_id}`); toast.success("Deleted"); signalTasksChanged(); load(); }} className="p-1.5 text-slate-400 hover:text-red-500"><Trash2 size={15} /></button>
                  </div>}
                </div>
              </div>
            ))}
        </TabsContent>

        {mine.linked && (
          <TabsContent value="mine" className="space-y-2" data-testid="my-prep-tasks">
            {mine.tasks.length === 0 ? <EmptyState icon={ListChecks} title="Nothing assigned to you." message="Prep tasks assigned to you will appear here." /> :
              mine.tasks.map((p) => (
                <div key={p.prep_id} className={`cc-card p-4 ${p.urgent ? "border-red-200" : ""}`} data-testid={`my-prep-${p.prep_id}`}>
                  <div className="flex items-start gap-3">
                    {canComplete && <Checkbox className="mt-1" data-testid={`my-complete-prep-${p.prep_id}`} onCheckedChange={() => completeTask(p.prep_id)} />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-800 break-words">{p.title}</span>
                        {p.urgent && <span className="text-[10px] font-bold uppercase bg-red-100 text-red-600 px-2 py-0.5 rounded-full flex items-center gap-1"><Flame size={11} /> Urgent</span>}
                      </div>
                      {p.details && <div className="text-xs text-slate-500 mt-0.5 break-words">{p.details}</div>}
                      <div className="text-[11px] text-slate-400 mt-1">{p.venue_name || "—"} · {p.priority} · Due {p.due_at ? relativeTime(p.due_at) : "—"} · {p.status}</div>
                    </div>
                  </div>
                </div>
              ))}
          </TabsContent>
        )}

        <TabsContent value="completed" className="space-y-2">
          {done.length === 0 ? <EmptyState icon={ListChecks} title="No completed tasks yet." /> :
            done.map((p) => (
              <div key={p.prep_id} className="cc-card p-4 flex items-center justify-between" data-testid={`prep-done-${p.prep_id}`}>
                <div className="min-w-0"><div className="font-semibold text-slate-700 line-through break-words">{p.title}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">
                    {p.assigned_to_name_snapshot && <span>Assigned to {p.assigned_to_name_snapshot} · </span>}
                    Completed by {p.completed_by_name} · {fmtDateTime(p.completed_at)} {p.late && <span className="text-amber-600 font-semibold">· late</span>}</div></div>
              </div>
            ))}
        </TabsContent>
      </Tabs>

      {(modal || edit) && <PrepModal task={edit} people={people} onClose={() => { setModal(false); setEdit(null); }} onSaved={() => { setModal(false); setEdit(null); load(); }} />}
      {batch && <BatchAddPrep people={people} onClose={() => setBatch(false)} onDone={load} />}
    </div>
  );
}

function BatchAddPrep({ people = [], onClose, onDone }) {
  const [existing, setExisting] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    api.get(withSite("/prep?status=active"))
      .then((r) => setExisting(r.data.map((p) => p.title)))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);
  const assigneeOptions = [{ value: "", label: "Unassigned" }, ...people.map((p) => ({ value: p.person_id, label: p.display_name }))];
  const columns = [
    { key: "title", label: "Task", placeholder: "e.g. Slice onions" },
    { key: "details", label: "Details", placeholder: "optional notes" },
    { key: "urgent", label: "Urgent", type: "select", default: "no",
      options: [{ value: "no", label: "No" }, { value: "yes", label: "Yes" }] },
    { key: "assigned_to_person_id", label: "Assignee", type: "select", default: "", options: assigneeOptions },
    { key: "due_at", label: "Due by", inputType: "datetime-local", placeholder: "", default: endOfTodayLocalInput() },
  ];
  const bulkFields = people.length > 0 ? [{ key: "assigned_to_person_id", label: "Assign all to", options: assigneeOptions }] : [];
  const validateRow = (v) => ({ errors: String(v.title || "").trim() ? {} : { title: "Required" }, warnings: [] });
  const annotate = (rows) => markDuplicates(rows, { field: "title", existing });
  const aliasMap = {
    title: ["item", "task", "prep item", "prep task", "description", "title", "name"],
    details: ["details", "notes", "note"],
    urgent: ["urgent", "priority"],
    due_at: ["due", "due by", "due at", "due date"],
  };
  const isUrgent = (v) => /^(yes|true|urgent|high|y|1)$/i.test(String(v ?? "").trim());
  if (!loaded) return null;
  const onCreate = async (subset, allRows) => {
    const payloadRows = subset.map((r) => {
      const raw = String(r.values.due_at || "").trim();
      let due = null;
      if (raw) { const d = new Date(raw); if (!isNaN(d.getTime())) due = d.toISOString(); }
      return {
        title: String(r.values.title || "").trim(), details: r.values.details || "",
        urgent: isUrgent(r.values.urgent), due_at: due,
        assigned_to_person_id: r.values.assigned_to_person_id || null,
      };
    });
    const operation_id = (crypto.randomUUID && crypto.randomUUID()) || `op_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    try {
      const { data } = await api.post(withSite("/prep/batch"), { operation_id, rows: payloadRows });
      const next = reconcile(allRows, subset, data.results || []);
      const parts = [`${data.created} created`];
      if (data.duplicates) parts.push(`${data.duplicates} duplicate`);
      if (data.failed) parts.push(`${data.failed} invalid`);
      const msg = parts.join(", ");
      if (data.created > 0) { toast.success(`Batch: ${msg}`); signalTasksChanged(); onDone && onDone(); }
      else toast.message(`Batch: ${msg}`);
      return next;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Batch add failed");
      return null;
    }
  };
  return (
    <BatchEditor
      columns={columns} validateRow={validateRow} annotate={annotate} pasteColumn="title" aliasMap={aliasMap} bulkFields={bulkFields}
      destinationLabel="Prep list" createLabel="Create tasks" onCreate={onCreate} onClose={onClose} />
  );
}

function PrepModal({ task, people = [], onClose, onSaved }) {
  const isEdit = !!task;
  const [f, setF] = useState(task
    ? { title: task.title || "", details: task.details || "", urgent: task.urgent || false,
        due_at: task.due_at || "", assigned_to_person_id: task.assigned_to_person_id || UNASSIGNED }
    : { title: "", details: "", urgent: false, due_at: endOfTodayLocalInput(), assigned_to_person_id: UNASSIGNED });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (saving) return;
    if (!f.title) return toast.error("Title required");
    const body = {
      title: f.title, details: f.details, urgent: f.urgent,
      due_at: f.due_at ? new Date(f.due_at).toISOString() : null,
      assigned_to_person_id: f.assigned_to_person_id === UNASSIGNED ? null : f.assigned_to_person_id,
    };
    const site = localStorage.getItem("cc_site");
    if (!isEdit && site) body.venue_id = site;
    setSaving(true);
    try {
      const { data } = isEdit ? await api.put(`/prep/${task.prep_id}`, body) : await api.post("/prep", body);
      toast.success(isEdit ? "Updated" : "Prep task added");
      if (data?.assignment_warning?.message) toast.warning(data.assignment_warning.message);
      signalTasksChanged();
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="prep-modal">
        <DialogHeader><DialogTitle>{isEdit ? "Edit prep task" : "Add prep task"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs font-semibold text-slate-600">Task</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="prep-title" className="mt-1.5" placeholder="e.g. Dauphinoise" /></div>
          <div><Label className="text-xs font-semibold text-slate-600">Details</Label><Textarea value={f.details} onChange={(e) => setF({ ...f, details: e.target.value })} data-testid="prep-details" className="mt-1.5" /></div>
          <div><Label className="text-xs font-semibold text-slate-600">Assign to (optional)</Label>
            <Select value={f.assigned_to_person_id} onValueChange={(v) => setF({ ...f, assigned_to_person_id: v })}>
              <SelectTrigger data-testid="prep-assignee" className="mt-1.5"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                {people.map((p) => <SelectItem key={p.person_id} value={p.person_id}>{p.display_name}{p.department ? ` · ${p.department}` : ""}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <div><Label className="text-xs font-semibold text-slate-600">Due by <span className="font-normal text-slate-400">(defaults to today)</span></Label><Input type="datetime-local" value={f.due_at ? String(f.due_at).slice(0, 16) : ""} onChange={(e) => setF({ ...f, due_at: e.target.value })} data-testid="prep-due" className="mt-1.5" /></div>
          <div className="flex items-center justify-between"><Label className="text-sm font-medium text-slate-700 flex items-center gap-2"><Flame size={15} className="text-red-500" /> Mark urgent</Label><Switch checked={f.urgent} onCheckedChange={(v) => setF({ ...f, urgent: v })} data-testid="prep-urgent" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={saving} data-testid="save-prep" className="bg-brand hover:bg-brand-hover">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
